import axios from 'axios';
import store from '@state/store';
import { logOut } from '@state/actions/authActions';
import { authTypes } from '@state/types';
import { API_BASE_URL, ensureTrailingSlash } from './config';
import { logRequest, logResponse, logError } from '../utils/logger';
import { captureException, isIgnorableSentryError } from '@lib/sentry';

const resolvedBaseUrl = ensureTrailingSlash(API_BASE_URL);
const SENSITIVE_KEY_PATTERN =
  /(?:access|refresh|auth|authorization|cookie|credential|password|secret|token|api[_-]?key|email|phone|address|name|message|prompt|content|document|case|user|company)/i;

// Leaving the page tears down every in-flight XHR. Depending on timing and the
// browser, Axios reports that as `ERR_NETWORK` / "Network Error" or
// `ECONNABORTED` / "Request aborted" - one rejection per request still open.
// The dashboard opens several in parallel on mount, and a single hand-off
// therefore reported a burst of errors for a document that no longer existed.
//
// Those carry no signal - the request got no response because the page went
// away, not because the API is broken - so they are dropped from monitoring
// only while the page is unloading. Every other transport failure, including a
// genuine CORS or DNS failure during normal page life, still reports.
let isPageUnloading = false;

const markPageUnloading = () => {
  isPageUnloading = true;
};

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', markPageUnloading);
  window.addEventListener('beforeunload', markPageUnloading);
  // A page restored from the back/forward cache is live again, so later
  // failures on it are real.
  window.addEventListener('pageshow', (event) => {
    if (event?.persisted) {
      isPageUnloading = false;
    }
  });
}

export const isPageTeardownNetworkError = (error) =>
  Boolean(
    isPageUnloading &&
      !error?.response &&
      (error?.code === 'ERR_NETWORK' ||
        error?.message === 'Network Error' ||
        (error?.code === 'ECONNABORTED' && error?.message === 'Request aborted'))
  );

const isLoginGraphQlUnauthorized = (error) => {
  if (
    error?.response?.status !== 401 ||
    error?.config?.method?.toLowerCase?.() !== 'post' ||
    typeof window === 'undefined' ||
    window.location.pathname.replace(/\/+$/, '') !== '/login'
  ) {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return requestPath.endsWith('/api/core/graphql');
};

// Public law summaries are intentionally rate-limited for automated scraping.
// A 429 (or the resulting blocked XHR) from the known ShapBot crawler is
// expected policy enforcement, not an application outage. Browser requests to
// the same endpoint can also legitimately hit its public 5/minute throttle;
// those are handled as an unavailable optional summary and aren't exceptions.
const isExpectedCrawlerSummaryFailure = (error) => {
  if (
    typeof window === 'undefined' ||
    !/\bShapBot\b/i.test(window.navigator?.userAgent || '')
  ) {
    return false;
  }

  const isRateLimit = error?.response?.status === 429;
  const isBlockedNetworkRequest =
    !error?.response &&
    (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error');
  if (!isRateLimit && !isBlockedNetworkRequest) return false;

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return requestPath.endsWith('/api/core/law-reader-summary');
};

const isExpectedLawEndpointResponse = (error) => {
  const status = error?.response?.status;
  if (status !== 404 && status !== 413 && status !== 422 && status !== 429) return false;

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return (
    ((status === 404 || status === 422) &&
      (requestPath.endsWith('/api/core/law-reader') ||
        requestPath.endsWith('/api/core/law-reader-summary'))) ||
    // The law Lambda intentionally returns 413 for documents too large to
    // inline; the law page then serves the same public file from the CDN.
    (status === 413 && requestPath.endsWith('/api/core/law-reader')) ||
    (status === 429 && requestPath.endsWith('/api/core/law-reader-summary'))
  );
};

const shouldRetryLawSummaryNetworkError = (error) => {
  if (
    error?.response ||
    error?.config?.method?.toLowerCase?.() !== 'get' ||
    error?.config?.__lawSummaryNetworkRetried ||
    isExpectedCrawlerSummaryFailure(error) ||
    (error?.code !== 'ERR_NETWORK' && error?.message !== 'Network Error')
  ) {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return requestPath.endsWith('/api/core/law-reader-summary');
};

const shouldRetryLawReaderNetworkError = (error) => {
  if (
    error?.response ||
    error?.config?.method?.toLowerCase?.() !== 'post' ||
    error?.config?.__lawReaderNetworkRetried ||
    (error?.code !== 'ERR_NETWORK' && error?.message !== 'Network Error')
  ) {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return requestPath.endsWith('/api/core/law-reader');
};

const getGraphQlRequestDocument = (config) => {
  let data = config?.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  return typeof data?.query === 'string' ? data.query : null;
};

const shouldRetryGraphQlReadNetworkError = (error) => {
  if (
    error?.response ||
    error?.config?.method?.toLowerCase?.() !== 'post' ||
    error?.config?.__graphQlReadNetworkRetried ||
    (error?.code !== 'ERR_NETWORK' && error?.message !== 'Network Error')
  ) {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  if (!requestPath.endsWith('/api/core/graphql')) return false;

  const document = getGraphQlRequestDocument(error.config);
  // GraphQL query operations are read-only by contract. Only retry documents
  // whose operation is unambiguously a query; a POST containing a mutation,
  // subscription, or unknown document must never be replayed automatically.
  return Boolean(
    document &&
      /^\s*(?:query\b|\{)/i.test(document) &&
      !/\b(?:mutation|subscription)\b/i.test(document),
  );
};

const shouldRetrySafeReadNetworkError = (error) => {
  if (
    error?.response ||
    error?.config?.__safeReadNetworkRetried ||
    (error?.code !== 'ERR_NETWORK' && error?.message !== 'Network Error')
  ) return false;
  const method = String(error?.config?.method || '').toLowerCase();
  if (method === 'get' || method === 'head' || method === 'options') return true;
  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  // Retrieving an existing case summary is read-only despite using POST for
  // its structured request body. A dropped response is safe to replay once.
  if (method === 'post' && requestPath.endsWith('/api/core/retrieve-case-summary')) {
    return true;
  }
  // GraphQL POST queries have their own operation-aware retry below.
  return false;
};

const isExpiredWhatsAppLink = (error) => {
  if (error?.response?.status !== 404 || error?.config?.method?.toLowerCase?.() !== 'post') {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return requestPath.endsWith('/api/core/integrations/whatsapp/vienna/link');
};

// The billing preflight deliberately returns this conflict when an account
// already has a global subscription. Its caller presents the existing-plan
// flow, so this is a handled business outcome rather than an application bug.
const isExistingGlobalSubscriptionConflict = (error) => {
  if (error?.response?.status !== 409 || error?.config?.method?.toLowerCase?.() !== 'post') {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return (
    requestPath.endsWith('/api/core/create-checkout-session') &&
    error?.response?.data?.code === 'GLOBAL_SUBSCRIPTION_EXISTS'
  );
};

// Receipt history is optional and a deleted local subscription row has no
// invoices to display. The Django view returns this exact response while it
// records a hashed warning for reconciliation.
const isMissingSubscriptionInvoiceHistory = (error) => {
  if (error?.response?.status !== 404 || error?.config?.method?.toLowerCase?.() !== 'post') {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  const detail = error?.response?.data?.detail;
  return (
    requestPath.endsWith('/api/core/stripe-subscription-invoices') &&
    detail === 'Subscription not found.'
  );
};

const getSafeApiErrorContext = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') return undefined;

  const isSafeCode = (value) =>
    typeof value === 'string' && /^[A-Za-z][A-Za-z0-9_.-]{0,79}$/.test(value);
  const graphQlErrors = Array.isArray(data.errors) ? data.errors : [];
  const graphQlErrorCodes = [...new Set(
    graphQlErrors
      .map((item) => item?.code ?? item?.extensions?.code ?? item?.extensions?.errorCode)
      .filter(isSafeCode)
  )].slice(0, 10);
  const unknownGraphQlFields = [...new Set(
    graphQlErrors
      .map((item) => String(item?.message || '').match(/cannot query field\s+['"]([^'"]+)['"]\s+on type\s+['"]([^'"]+)['"]/i))
      .filter(Boolean)
      .map((match) => `${match[2]}.${match[1]}`)
      .filter((field) => /^[A-Za-z][A-Za-z0-9_.-]{0,159}$/.test(field))
  )].slice(0, 10);
  const graphQlPaths = [...new Set(
    graphQlErrors
      .map((item) => item?.path)
      .filter(Array.isArray)
      .map((path) => path.filter((part) => typeof part === 'string' && isSafeCode(part)).join('.'))
      .filter(Boolean)
  )].slice(0, 10);
  // Several REST endpoints return machine-readable failures in `error` rather
  // than `code`; only retain the identifier-shaped value so response text and
  // user data never enter Sentry context.
  const responseCode = data.code ?? data.error_code ?? data.error;
  const context = {};
  const responseFields = Object.keys(data)
    .filter((field) => isSafeCode(field) && !SENSITIVE_KEY_PATTERN.test(field))
    .slice(0, 20);
  const validationFields = responseFields.filter((field) => {
    const value = data[field];
    return Array.isArray(value) || (value !== null && typeof value === 'object');
  });

  if (isSafeCode(responseCode)) context.code = responseCode;
  // DRF validation errors commonly map field names to arrays of human-readable
  // messages. Record only the field names, never their values, so a 400 points
  // to the failing contract without sending request or user data to Sentry.
  if (responseFields.length) context.responseFields = responseFields;
  if (validationFields.length) context.validationFields = validationFields;
  if (graphQlErrors.length) context.graphQlErrorCount = graphQlErrors.length;
  if (graphQlErrorCodes.length) context.graphQlErrorCodes = graphQlErrorCodes;
  if (unknownGraphQlFields.length) context.unknownGraphQlFields = unknownGraphQlFields;
  if (graphQlPaths.length) context.graphQlPaths = graphQlPaths;

  return Object.keys(context).length ? context : undefined;
};

const getSafeGraphQlOperationName = (config) => {
  const requestPath = String(config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  if (!requestPath.endsWith('/api/core/graphql')) return undefined;

  const document = getGraphQlRequestDocument(config);
  const match = document?.match(/^\s*query\s+([_A-Za-z][_0-9A-Za-z]*)\b/);
  return match?.[1] && /^[_A-Za-z][_0-9A-Za-z]{0,79}$/.test(match[1])
    ? match[1]
    : undefined;
};

const getSafeRequestUrl = (config) => {
  const requestUrl = typeof config?.url === 'string' ? config.url : '';
  if (!requestUrl) return undefined;
  try {
    const parsed = new URL(requestUrl, config?.baseURL || resolvedBaseUrl);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return requestUrl.split(/[?#]/, 1)[0];
  }
};

const getSafeLawRequestContext = (error) => {
  if (error?.config?.method?.toLowerCase?.() !== 'post') return undefined;

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  if (!requestPath.endsWith('/api/core/law-reader')) return undefined;

  let data = error.config.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return undefined;
    }
  }

  if (typeof data?.path !== 'string') return undefined;
  const path = data.path.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 500);
  return path ? { path } : undefined;
};

const getSafeInvoiceHistoryErrorContext = (error) => {
  if (
    error?.response?.status !== 404 ||
    error?.config?.method?.toLowerCase?.() !== 'post'
  ) {
    return undefined;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  if (!requestPath.endsWith('/api/core/stripe-subscription-invoices')) return undefined;

  const data = error.response?.data;
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  const outcome =
    detail === 'Subscription not found.'
      ? 'subscription_missing'
      : detail
        ? 'other_detail'
        : data && typeof data === 'object'
          ? 'detail_missing'
          : 'non_json_response';

  return { outcome };
};

const getKnownApiErrorFingerprint = (error, invoiceHistoryErrorContext) => {
  const hasResponse = Boolean(error?.response);
  const transportCode = typeof error?.code === 'string' && /^[A-Za-z0-9_.-]{1,80}$/.test(error.code)
    ? error.code.toUpperCase()
    : '';
  const transportMessage = String(error?.message || '');
  if (
    !hasResponse &&
    !transportCode &&
    transportMessage !== 'Network Error' &&
    transportMessage !== 'Request aborted'
  ) return undefined;

  const method = String(error?.config?.method || 'request').toLowerCase();
  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  const isLawReader = requestPath.endsWith('/api/core/law-reader');
  const isInvoiceHistory = requestPath.endsWith('/api/core/stripe-subscription-invoices');
  const operation = getSafeGraphQlOperationName(error.config);
  const endpoint = operation
    ? `graphql:${operation}`
    : requestPath
      .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}(?=\/|$)/gi, '/:id')
      .replace(/\/\d+(?=\/|$)/g, '/:id')
      .replace(/\/[0-9a-f]{16,}(?=\/|$)/gi, '/:id')
      .replace(/\/[^/]+@[^/]+(?=\/|$)/g, '/:id')
      .slice(0, 180);
  const outcome = hasResponse
    ? String(error.response.status)
    : `transport:${transportCode || (transportMessage === 'Request aborted' ? 'aborted' : 'network')}`;

  return [
    'axios-request',
    method,
    isLawReader ? 'law-reader' : isInvoiceHistory ? 'stripe-subscription-invoices' : endpoint || 'unknown-endpoint',
    outcome,
    ...(invoiceHistoryErrorContext ? [invoiceHistoryErrorContext.outcome] : []),
  ];
};

// Profile and access queries can target backend deployments with an older
// GraphQL schema. Their callers retry after removing optional fields, so the
// initial validation 400 is expected only when every rejected field is one
// that the caller will remove and retry without.
const isExpectedGraphQlSchemaFallback = (error) => {
  if (
    error?.response?.status !== 400 ||
    error?.config?.method?.toLowerCase?.() !== 'post' ||
    !Array.isArray(error?.config?.sentryIgnoreGraphQlSchemaMismatchFields)
  ) {
    return false;
  }

  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  if (!requestPath.endsWith('/api/core/graphql')) return false;

  let data = error.response?.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return false;
    }
  }
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  const retryableFields = new Set(error.config.sentryIgnoreGraphQlSchemaMismatchFields);
  if (errors.length === 0) return false;

  return errors.every(({ message = '' } = {}) => {
    const match = String(message).match(
      /Cannot query field\s+['\"]([^'\"]+)['\"]\s+on type\s+['\"]?([A-Za-z][A-Za-z0-9_]*)/i,
    );
    return Boolean(match && retryableFields.has(`${match[2]}.${match[1]}`));
  });
};

// Compatibility export for the shared Vienna public-tool API modules. Home's
// existing config remains the source of truth for the API origin.
export const resolveApiBaseUrl = () => resolvedBaseUrl;

const axiosInstance = axios.create({
  baseURL: resolvedBaseUrl || undefined,
});

const handleLogout = () => {
  try {
    store.dispatch(logOut());
  } catch (error) {
    logError(error);
  }
};

// A 401 from a request-scoped chat endpoint must not tear down an otherwise
// valid dashboard session. Only an explicit access-token failure is a session
// failure; permission, stale-run, and other endpoint-level 401s are surfaced to
// their caller instead.
const shouldLogoutOnUnauthorized = (error) => {
  if (error?.response?.status !== 401) {
    return false;
  }

  const state = store.getState ? store.getState() : {};
  const activeAccessToken = state?.auth?.access || window.localStorage.getItem('access');
  const requestUrl = `${error?.config?.baseURL || ''}${error?.config?.url || ''}`;

  // Chat runs can outlive a tab/request and return 401 for a stale run. Never
  // log out a user from that background path while an access token is active.
  if (activeAccessToken && requestUrl.includes('/api/core/chat/runs')) {
    return false;
  }

  const data = error.response?.data || {};
  const code = typeof data.code === 'string' ? data.code.toLowerCase() : '';
  const detail = typeof data.detail === 'string' ? data.detail.toLowerCase() : '';
  const message = typeof data.message === 'string' ? data.message.toLowerCase() : '';
  const hasExpiredAccessMessage = Array.isArray(data.messages) && data.messages.some((entry) => {
    const tokenType = typeof entry?.token_type === 'string' ? entry.token_type.toLowerCase() : '';
    const entryMessage = typeof entry?.message === 'string' ? entry.message.toLowerCase() : '';
    return tokenType === 'access' && (entryMessage.includes('invalid') || entryMessage.includes('expired'));
  });
  const hasGraphqlJwtAuthError =
    Array.isArray(data.errors) &&
    data.errors.some((entry) => {
      const graphqlMessage = typeof entry?.message === 'string' ? entry.message.toLowerCase() : '';
      return (
        graphqlMessage.includes('authentication required') &&
        graphqlMessage.includes('valid jwt token auth header')
      );
    });

  // The API sometimes returns authentication failures as a GraphQL error with
  // HTTP 401, but omits the JWT wording above. Refreshing the access token is a
  // safe one-time recovery for the private GraphQL endpoint; without this
  // fallback, authenticated access checks fail immediately when a token expires.
  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  const isPrivateGraphQlUnauthorized =
    error?.response?.status === 401 &&
    error?.config?.method?.toLowerCase?.() === 'post' &&
    requestPath.endsWith('/api/core/graphql');

  return (
    code === 'token_not_valid' ||
    detail.includes('token') ||
    message.includes('token') ||
    hasExpiredAccessMessage ||
    hasGraphqlJwtAuthError ||
    isPrivateGraphQlUnauthorized
  );
};

const REFRESH_ENDPOINT = 'auth/token/refresh/';
let inFlightRefresh = null;
let inFlightChatTokenRefresh = null;

const getStoredRefreshToken = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const token = store.getState?.().auth?.refresh || window.localStorage.getItem('refresh');
    return token ? String(token).trim() : null;
  } catch (error) {
    return null;
  }
};

const refreshAccessToken = () => {
  if (inFlightRefresh) return inFlightRefresh;

  const refresh = getStoredRefreshToken();
  if (!refresh) return Promise.resolve(null);

  inFlightRefresh = axios
    .post(`${resolvedBaseUrl}${REFRESH_ENDPOINT}`, { refresh }, { withCredentials: true })
    .then((response) => {
      const access = response?.data?.access ? String(response.data.access).trim() : null;
      if (!access) return null;

      const patch = { access, isAuthenticated: true };
      if (response.data.access_expiration) patch.access_expiration = response.data.access_expiration;
      if (response.data.refresh) patch.refresh = response.data.refresh;
      if (response.data.refresh_expiration) patch.refresh_expiration = response.data.refresh_expiration;

      try {
        Object.entries(patch).forEach(([key, value]) => window.localStorage.setItem(key, value));
      } catch (error) {
        // Redux still carries the refreshed access token for this session.
      }
      store.dispatch({ type: authTypes.REFRESH_TOKEN_SUCCESS, payload: patch });
      return access;
    })
    .catch(() => null)
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
};

const refreshChatToken = () => {
  if (inFlightChatTokenRefresh) return inFlightChatTokenRefresh;

  inFlightChatTokenRefresh = (async () => {
    let accessToken =
      (store.getState ? store.getState() : {})?.auth?.access ||
      window.localStorage.getItem('access');
    if (!accessToken) throw new Error('An authenticated session is required to refresh chat access.');

    let response;
    try {
      response = await axios.get(`${resolvedBaseUrl}api/core/chat/token/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
      accessToken = await refreshAccessToken();
      if (!accessToken) throw error;
      response = await axios.get(`${resolvedBaseUrl}api/core/chat/token/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    }

    const chatToken = response?.data?.chat_token;
    if (typeof chatToken !== 'string' || !chatToken.trim()) {
      throw new Error('The chat token endpoint returned no token.');
    }
    window.localStorage.setItem('chatToken', chatToken);
    store.dispatch({
      type: authTypes.REFRESH_TOKEN_SUCCESS,
      payload: { chatToken },
    });
    return chatToken;
  })().finally(() => {
    inFlightChatTokenRefresh = null;
  });

  return inFlightChatTokenRefresh;
};

const isChatProxyRequest = (error) => {
  if (error?.response?.status !== 401 || error?.config?.__chatTokenRetried) return false;
  const requestPath = String(error?.config?.url || '')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/+$/, '');
  return requestPath.startsWith('/chat/');
};

axiosInstance.interceptors.request.use(
  (config) => {
    const updatedConfig = { ...config };

    if (!updatedConfig.headers) {
      updatedConfig.headers = {};
    }

    if (!updatedConfig.headers.Authorization) {
      try {
        const state = store.getState ? store.getState() : {};
        const token = state?.auth?.access || window.localStorage.getItem('access');
        if (token) {
          updatedConfig.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        logError(error);
      }
    }

    logRequest(updatedConfig.url, updatedConfig.method, updatedConfig.headers, updatedConfig.data);

    return updatedConfig;
  },
  (error) => {
    logError(error);

    if (!isIgnorableSentryError(error) && !isPageTeardownNetworkError(error)) {
      captureException(error, {
        tags: { source: 'axios', phase: 'request' },
        contexts: { request: { method: error?.config?.method, url: error?.config?.url } },
      });
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    logResponse(response);
    return response;
  },
  async (error) => {
    logError(error);

    if (shouldRetrySafeReadNetworkError(error) && !isPageTeardownNetworkError(error)) {
      const config = error.config;
      config.__safeReadNetworkRetried = true;
      await new Promise((resolve) => setTimeout(resolve, 300));
      return axiosInstance(config);
    }

    // Law-reader POSTs retrieve public legal text and do not mutate state. A
    // single retry absorbs transient browser transport drops without risking
    // duplicate writes; repeated failures continue to the Sentry path below.
    if (
      shouldRetryLawReaderNetworkError(error) &&
      !isPageTeardownNetworkError(error)
    ) {
      const config = error.config;
      config.__lawReaderNetworkRetried = true;
      await new Promise((resolve) => setTimeout(resolve, 250));
      return axiosInstance(config);
    }

    // This optional public GET occasionally fails during a transient mobile
    // connection drop. Retry once; persistent network failures still reach
    // Sentry with the request context below.
    if (
      shouldRetryLawSummaryNetworkError(error) &&
      !isPageTeardownNetworkError(error)
    ) {
      const config = error.config;
      config.__lawSummaryNetworkRetried = true;
      await new Promise((resolve) => setTimeout(resolve, 250));
      return axiosInstance(config);
    }

    // GraphQL queries are read-only, so one retry can recover a transient
    // transport drop without risking duplicate mutations. Unknown, mutation,
    // and subscription operations continue to the normal error path.
    if (
      shouldRetryGraphQlReadNetworkError(error) &&
      !isPageTeardownNetworkError(error)
    ) {
      const config = error.config;
      config.__graphQlReadNetworkRetried = true;
      await new Promise((resolve) => setTimeout(resolve, 250));
      return axiosInstance(config);
    }

    // LibreChat JWTs expire independently from the Django access token. Renew
    // the JWT through the authenticated Django session and replay the original
    // proxy request once so a stale chat token does not become a Sentry error.
    if (isChatProxyRequest(error)) {
      try {
        const chatToken = await refreshChatToken();
        const config = error.config;
        config.__chatTokenRetried = true;
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${chatToken}` };
        return axiosInstance(config);
      } catch (_refreshError) {
        // Fall through to the normal reporting path if renewal or replay fails.
      }
    }

    if (shouldLogoutOnUnauthorized(error)) {
      // Only logout if the token that was sent with this request is still
      // the current active token. If a new login happened after this request
      // was fired, a stale 401 from the old session must not wipe the new one.
      const requestToken = error?.config?.headers?.Authorization?.replace(/^Bearer\s+/i, '');
      const currentToken =
        (store.getState ? store.getState() : {})?.auth?.access ||
        window.localStorage.getItem('access');

      const tokenStillCurrent =
        !requestToken || !currentToken || requestToken === currentToken;

      if (tokenStillCurrent) {
        const config = error?.config || {};
        if (!config.__accessRefreshRetried && getStoredRefreshToken()) {
          const access = await refreshAccessToken();
          if (access) {
            config.__accessRefreshRetried = true;
            config.headers = { ...(config.headers || {}), Authorization: `Bearer ${access}` };
            return axiosInstance(config);
          }
        }
        handleLogout();
      }
    }

    if (
      !isIgnorableSentryError(error) &&
      !isPageTeardownNetworkError(error) &&
      !isLoginGraphQlUnauthorized(error) &&
      !isExpectedCrawlerSummaryFailure(error) &&
      !isExpectedLawEndpointResponse(error) &&
      !isExpiredWhatsAppLink(error) &&
      !isExistingGlobalSubscriptionConflict(error) &&
      !isMissingSubscriptionInvoiceHistory(error) &&
      !isExpectedGraphQlSchemaFallback(error)
    ) {
      const apiErrorContext = getSafeApiErrorContext(error);
      const lawRequestContext = getSafeLawRequestContext(error);
      const invoiceHistoryErrorContext = getSafeInvoiceHistoryErrorContext(error);
      const fingerprint = getKnownApiErrorFingerprint(error, invoiceHistoryErrorContext);
      captureException(error, {
        tags: { source: 'axios', phase: 'response' },
        ...(fingerprint ? { fingerprint } : {}),
        contexts: {
          request: {
            method: error?.config?.method,
            url: getSafeRequestUrl(error?.config),
            status: error?.response?.status,
            ...(getSafeGraphQlOperationName(error?.config)
              ? { graphQlOperation: getSafeGraphQlOperationName(error.config) }
              : {}),
            ...(typeof error?.code === 'string' && /^[A-Za-z0-9_.-]{1,80}$/.test(error.code)
              ? { transportCode: error.code }
              : {}),
            ...(typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
              ? { browserOnline: navigator.onLine }
              : {}),
          },
          ...(lawRequestContext ? { law_request: lawRequestContext } : {}),
          ...(invoiceHistoryErrorContext
            ? { invoice_history_error: invoiceHistoryErrorContext }
            : {}),
          ...(apiErrorContext ? { api_error: apiErrorContext } : {}),
        },
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
